import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons/faEllipsisH';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { Download } from '../../../helpers/download/download';
import { JobResultInterface } from '../../../models/interface/jobresult.interface';
import { catchError, delay, filter, mergeMap, repeat, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GetAllJobResultsOfUserPayload, QueueActionTypes } from '../../../store/queue/queue.action';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

type ResponseState = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

export type DialogInterventionInstanceReportData = {
  instance: InterventionInstanceInterface;
  intervention: InterventionInterface
  collaborators: UserInterface[];
  users: UserInterface[];
};

@Component({
  selector: 'app-dialog-intervention-instance-report',
  templateUrl: './dialog-intervention-instance-report.component.html',
  styleUrls: ['./dialog-intervention-instance-report.component.scss'],
  providers: [InterventionInstanceStore],
})
export class DialogInterventionInstanceReportComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faFilePdf = faFilePdf;
  faHands = faHands;
  faHandsHelping = faHandsHelping;
  faTimes = faTimes;
  faEllipsisH = faEllipsisH;
  faRedo = faRedo;
  faClock = faClock;

  public isLoading$: Observable<boolean>;

  public param: { intervention_name: string };
  public collaborators: UserInterface[];
  public users: UserInterface[];
  user: UserInterface = null;

  public instance: InterventionInstanceInterface;

  public intervention: InterventionInterface = null;

  public isReviewer = false;
  public isReviewerCollaborator = false;

  public includeUnfinishedQuestionnaires = false;
  public includeActivities = false;
  public includeDiaries = false;
  public includeFeedback = false;

  // Form submission
  public submitted = false;

  public downloadPDFReportResponse = new BehaviorSubject<ResponseState>('DEFAULT');

  public download$: Observable<Download>;
  public jobresults$: Observable<JobResultInterface[]>;

  public selectedTabIndex = 0;

  public showDownloadHintSubject = new BehaviorSubject<boolean>(false);

  public pagedJobResults$: Observable<JobResultInterface[]>;
  public pagedJobResultsSubject = new BehaviorSubject<JobResultInterface[]>([]);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private getInstancePDFReportQueueResponse$: Observable<any>;

  private jobresultsSubject = new BehaviorSubject<JobResultInterface[]>([]);

  // Subscription Handler
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogInterventionInstanceReportData,
    private helperService: HelperService,
    private interventionInstanceStore: InterventionInstanceStore,
    private actions$: Actions,
    private store: Store,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.jobresults$ = this.jobresultsSubject.asObservable();
    this.getInstancePDFReportQueueResponse$ = this.interventionInstanceStore.getInstancePDFReportQueueResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.instance = this.data.instance;
    this.intervention = this.data.intervention;
    this.param = { intervention_name: this.intervention.attributes.title };
    this.collaborators = this.data.collaborators;
    this.users = this.data.users;
    this.user = this.helper.findArrObjById(this.instance.attributes.patient_id, this.users);
    const poll = of({}).pipe(
      mergeMap(() => {
        if (this.jobresultsSubject.value.some((job) => job.attributes.finished === 0)) {
          this.loadJobResults();
        }
        return of({});
      }),
      catchError(() => of(null)),
      delay(30000),
      repeat(),
    );
    this.subscriptions.push(poll.subscribe());
    this.pagedJobResults$ = this.pagedJobResultsSubject.asObservable();
    this.loadJobResults();
  }

  public getInstanceReportPDF(instance: InterventionInstanceInterface): void {
    if (this.downloadPDFReportResponse.value === 'FAILURE' || this.downloadPDFReportResponse.value === 'SUCCESS') {
      this.downloadPDFReportResponse.next('DEFAULT');
      return;
    }
    this.showDownloadHintSubject.next(false);
    const unfinishedQuestionnaires = this.includeUnfinishedQuestionnaires ? 1 : undefined;
    const activities = this.includeActivities ? 1 : undefined;
    const diaries = this.includeDiaries ? 1 : undefined;
    const feedback = this.includeFeedback ? 1 : undefined;
    this.submitted = true;
    if (this.downloadPDFReportResponse.value === 'DEFAULT') {
      this.downloadPDFReportResponse.next('LOADING');
      this.interventionInstanceStore.getInstancePDFReportQueue({
        instanceId: instance.id,
        unfinishedQuestionnaires,
        activities,
        diaries,
        feedback,
      });
      this.subscriptions.push(
        this.getInstancePDFReportQueueResponse$
          .pipe(
            filter((value) => !!value),
            mergeMap((result: any) => {
              if (result instanceof HttpErrorResponse) {
                return throwError(() => result);
              } else {
                return this.requestJobResults();
              }
            }),
          )
          .subscribe({
            next: (success) => {
              if (success) {
                this.downloadPDFReportResponse.next('SUCCESS');
                this.showDownloadHintSubject.next(true);
                this.isLoadingSubject.next(false);
              } else {
                this.downloadPDFReportResponse.next('FAILURE');
              }
              setTimeout(() => {
                this.downloadPDFReportResponse.next('DEFAULT');
                this.submitted = false;
              }, 2500);
            },
            error: () => {
              this.downloadPDFReportResponse.next('FAILURE');
              setTimeout(() => {
                this.downloadPDFReportResponse.next('DEFAULT');
                this.submitted = false;
              }, 2500);
            },
          }),
      );
    }
  }

  public updatePagedJobInstances(event: JobResultInterface[]) {
    if (event) {
      this.pagedJobResultsSubject.next(event);
    }
  }

  private requestJobResults(): Observable<boolean> {
    const payload: GetAllJobResultsOfUserPayload = {
      jobType: 'Intervention_Instance',
      entityId: this.instance.id,
    };
    this.store.dispatch({
      type: QueueActionTypes.getAllJobResultsOfUserType,
      payload,
    });
    return this.actions$
      .pipe(
        ofType(
          QueueActionTypes.getAllJobResultsOfUserSuccessType,
          QueueActionTypes.getAllJobResultsOfUserErrorType,
        ),
        take(1),
        mergeMap((result: any) => {
          if (result.type === QueueActionTypes.getAllJobResultsOfUserSuccessType) {
            const allJobs: JobResultInterface[] = result['response']['body']['data'];
            const sortedJobs = allJobs.sort(this.helper.sortByIdDesc);
            this.jobresultsSubject.next(sortedJobs);
            this.pagedJobResultsSubject.next(this.jobresultsSubject.value.slice(0, 20));
            this.paginator?.paginator?.firstPage();
            return of(true);
          } else {
            return of(false);
          }
        }),
      );
  }

  loadJobResults(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.requestJobResults().subscribe((success) => {
        if (success) {
          this.isLoadingSubject.next(false);
        }
      }),
    );
  }

  public setSelectedTabIndex(event: number): void {
    this.selectedTabIndex = event;
    this.showDownloadHintSubject.next(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
